// Global Instructions Rule Applied!
//
// Agreement-set editor (Phase 5, signed-off Mockup 5.2). Manual capture only
// (owner ruling — no agent extraction for rights): header (name, round, deal,
// executed date, notes) + the full rights list, saved in one full-replace
// call (legal__save_set). Existing right ids are preserved so cross-right
// references (basis 'right_reference') stay stable across saves. Source
// provenance per right = an executed document already attached to one of the
// company's deals, or a pasted URL, plus a typed section reference.
import React, { useEffect, useState } from 'react'
import { Button, Input, Modal, Radio, Select, Switch } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import InputNumber from '../../../core/components/InputNumber.jsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  LegalCompanyDeal,
  LegalCompanyDocument,
  LegalCompanyRound,
  LegalRightTypeOption,
  LegalRuleVariable
} from '../../../state_manager/data_models/legal_rights_model.ts'
import { LegalRightSavePayload, LegalSetSavePayload, saveLegalSet } from '../services/dealExecutionService.ts'

const BASIS_OPTIONS = [
  { value: 'absolute', label: 'Absolute' },
  { value: 'rule', label: 'Rule' },
  { value: 'right_reference', label: 'References' }
]

const OPERATOR_OPTIONS = [
  { value: '>=', label: '≥' },
  { value: '>', label: '>' },
  { value: '<=', label: '≤' },
  { value: '<', label: '<' },
  { value: '=', label: '=' }
]

interface DraftRight {
  key: string
  id?: string
  right_type_id: string | null
  name: string
  basis: 'absolute' | 'rule' | 'right_reference'
  rule_variable: string | null
  rule_operator: string
  rule_threshold: number | null
  referenced_right_id: string | null
  reference_text: string | null
  carried: boolean
  section_ref: string | null
  source_file_id: string | null
  source_url: string | null
}

let keyCounter = 0
function nextKey(): string {
  keyCounter += 1
  return `lr${keyCounter}`
}

function numberFromBlur(e: React.FocusEvent<HTMLInputElement>): number | null {
  const raw = e.target.value.replace(/[^0-9.-]/g, '')
  return raw === '' ? null : Number(raw)
}

export interface LegalReferenceOption {
  value: string
  label: string
}

interface LegalSetEditorModalProps {
  open: boolean
  accountId: string
  initial: LegalSetSavePayload | null
  rightTypes: LegalRightTypeOption[]
  ruleVariables: LegalRuleVariable[]
  companyRounds: LegalCompanyRound[]
  companyDeals: LegalCompanyDeal[]
  companyDocuments: LegalCompanyDocument[]
  referenceOptions: LegalReferenceOption[]
  /** Refetch the parent's legal payload (which carries company_rounds) when
   *  the round dropdown opens — the local standard for pickers whose options
   *  were fetched at mount and go stale (see useAccountOptions): a round
   *  created after this payload loaded would otherwise never appear without
   *  a hard refresh. */
  onRefreshRounds?: (() => Promise<void> | void) | null
  onClose: () => void
  onSaved: () => void
}

export default function LegalSetEditorModal({
  open,
  accountId,
  initial,
  rightTypes,
  ruleVariables,
  companyRounds,
  companyDeals,
  companyDocuments,
  referenceOptions,
  onRefreshRounds = null,
  onClose,
  onSaved
}: LegalSetEditorModalProps): React.ReactElement {
  const [name, setName] = useState('')
  const [roundId, setRoundId] = useState<string | null>(null)
  const [dealId, setDealId] = useState<string | null>(null)
  const [executedDate, setExecutedDate] = useState<string | null>(null)
  const [notes, setNotes] = useState('')
  const [rights, setRights] = useState<DraftRight[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setName(initial?.name || '')
    setRoundId(initial?.round_id ?? null)
    setDealId(initial?.deal_id ?? null)
    setExecutedDate(initial?.executed_date ?? null)
    setNotes(initial?.notes || '')
    setRights(
      (initial?.rights || []).map((r) => ({
        key: nextKey(),
        id: r.id,
        right_type_id: r.right_type_id ?? null,
        name: r.name,
        basis: r.basis,
        rule_variable: r.rule_variable ?? null,
        rule_operator: r.rule_operator || '>=',
        rule_threshold: r.rule_threshold ?? null,
        referenced_right_id: r.referenced_right_id ?? null,
        reference_text: r.reference_text ?? null,
        carried: r.carried ?? false,
        section_ref: r.section_ref ?? null,
        source_file_id: r.source_file_id ?? null,
        source_url: r.source_url ?? null
      }))
    )
  }, [open, initial])

  const patchRight = (key: string, patch: Partial<DraftRight>): void => {
    setRights((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }

  const addRight = (): void => {
    setRights((prev) => [
      ...prev,
      {
        key: nextKey(),
        right_type_id: null,
        name: '',
        basis: 'absolute',
        rule_variable: null,
        rule_operator: '>=',
        rule_threshold: null,
        referenced_right_id: null,
        reference_text: null,
        carried: false,
        section_ref: null,
        source_file_id: null,
        source_url: null
      }
    ])
  }

  const applyType = (key: string, typeId: string | null): void => {
    const type = rightTypes.find((t) => t.value === typeId)
    setRights((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r
        const next: DraftRight = { ...r, right_type_id: typeId }
        if (type && !r.name) next.name = type.label
        if (type?.default_basis && r.basis === 'absolute' && !r.id) {
          next.basis = type.default_basis as DraftRight['basis']
        }
        return next
      })
    )
  }

  const save = async (): Promise<void> => {
    if (!name.trim()) {
      showErrorToast('The agreement set needs a name')
      return
    }
    const bad = rights.find((r) => !r.name.trim())
    if (bad) {
      showErrorToast('Every right needs a name')
      return
    }
    const badRule = rights.find(
      (r) => r.basis === 'rule' && (!r.rule_variable || r.rule_threshold == null)
    )
    if (badRule) {
      showErrorToast(`"${badRule.name}" needs a variable and a threshold`)
      return
    }
    setSaving(true)
    const payload: LegalSetSavePayload = {
      id: initial?.id,
      account_id: accountId,
      round_id: roundId,
      deal_id: dealId,
      name: name.trim(),
      executed_date: executedDate,
      notes: notes.trim() || null,
      rights: rights.map((r, i): LegalRightSavePayload => ({
        id: r.id,
        right_type_id: r.right_type_id,
        name: r.name.trim(),
        basis: r.basis,
        rule_variable: r.basis === 'rule' ? r.rule_variable : null,
        rule_operator: r.rule_operator,
        rule_threshold: r.basis === 'rule' ? r.rule_threshold : null,
        referenced_right_id: r.basis === 'right_reference' ? r.referenced_right_id : null,
        reference_text: r.basis === 'right_reference' ? r.reference_text : null,
        carried: r.carried,
        section_ref: r.section_ref,
        source_file_id: r.source_file_id,
        source_url: r.source_url,
        sort_order: (i + 1) * 10
      }))
    }
    const token = getAccessToken(store.getState().auth)
    const result = await saveLegalSet(token, payload)
    setSaving(false)
    if (result.error || !result.setId) {
      showErrorToast(result.error || 'Failed to save agreement set')
      return
    }
    toast.success('Agreement set saved')
    onClose()
    onSaved()
  }

  const roundOptions = companyRounds.map((r) => ({ value: r.rounds__id, label: r.rounds__name || '—' }))
  const dealOptions = companyDeals.map((d) => ({ value: d.deals__id, label: d.deals__name || '—' }))
  const documentOptions = companyDocuments.map((f) => ({
    value: f.file_urls_deals__id,
    label:
      `${f.file_urls_deals__file_name || 'Untitled'}` +
      (f.file_urls_deals__lifecycle ? ` — ${f.file_urls_deals__lifecycle}` : '') +
      (f.deals__name ? ` · ${f.deals__name}` : '')
  }))
  const variableOptions = ruleVariables.map((v) => ({ value: v.key, label: v.label }))

  return (
    <Modal
      title={initial?.id ? 'Edit Agreement Set' : 'New Agreement Set'}
      open={open}
      onCancel={onClose}
      width={980}
      footer={[
        <Button key='cancel' onClick={onClose}>
          Cancel
        </Button>,
        <Button key='save' type='primary' loading={saving} onClick={save}>
          Save
        </Button>
      ]}
    >
      <div className='space-y-4'>
        {/* Header fields: labels beside inputs (firm convention) */}
        <div className='grid grid-cols-[110px_1fr_110px_1fr] gap-x-2 gap-y-2 items-center'>
          <span className='text-xs text-gray-500'>Name</span>
          <Input size='small' value={name} onChange={(e) => setName(e.target.value)} />
          <span className='text-xs text-gray-500'>Executed Date</span>
          <DatePicker
            size='small'
            className='w-full'
            format='MM/DD/YYYY'
            value={executedDate ? dayjs(executedDate) : null}
            onChange={(d) => setExecutedDate(d ? d.format('YYYY-MM-DD') : null)}
          />
          <span className='text-xs text-gray-500'>Round</span>
          <Select
            size='small'
            className='w-full'
            allowClear
            showSearch
            optionFilterProp='label'
            placeholder='Optional round…'
            value={roundId ?? undefined}
            options={roundOptions}
            onChange={(v) => setRoundId(v ?? null)}
            onDropdownVisibleChange={(visible) => {
              if (visible && onRefreshRounds) void onRefreshRounds()
            }}
          />
          <span className='text-xs text-gray-500'>Deal</span>
          <Select
            size='small'
            className='w-full'
            allowClear
            showSearch
            optionFilterProp='label'
            placeholder='Optional deal…'
            value={dealId ?? undefined}
            options={dealOptions}
            onChange={(v) => setDealId(v ?? null)}
          />
          <span className='text-xs text-gray-500'>Notes</span>
          <Input
            size='small'
            className='col-span-3'
            placeholder='Optional notes…'
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        {/* Rights list */}
        <div className='border border-gray-200 rounded'>
          <div className='flex items-center justify-between px-3 py-1.5 border-b border-gray-200'>
            <span className='text-sm font-medium'>Rights</span>
            <Button size='small' icon={<PlusOutlined />} onClick={addRight}>
              Add Right
            </Button>
          </div>
          {rights.length === 0 && (
            <div className='text-sm text-gray-400 px-3 py-4'>
              No rights captured yet — add the rights this agreement set grants.
            </div>
          )}
          {rights.map((r) => (
            <div key={r.key} className='px-3 py-3 border-b border-gray-100 last:border-b-0 space-y-2'>
              <div className='flex items-center gap-2'>
                <Select
                  size='small'
                  className='w-56'
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  placeholder='Right type…'
                  value={r.right_type_id ?? undefined}
                  options={rightTypes}
                  onChange={(v) => applyType(r.key, v ?? null)}
                />
                <Input
                  size='small'
                  className='flex-1'
                  placeholder='Right name'
                  value={r.name}
                  onChange={(e) => patchRight(r.key, { name: e.target.value })}
                />
                <span className='text-xs text-gray-500 whitespace-nowrap'>Carried</span>
                <Switch
                  size='small'
                  checked={r.carried}
                  onChange={(v) => patchRight(r.key, { carried: v })}
                />
                <Button
                  size='small'
                  type='text'
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => setRights((prev) => prev.filter((x) => x.key !== r.key))}
                />
              </div>
              <div className='flex items-center gap-3 flex-wrap'>
                <Radio.Group
                  size='small'
                  optionType='button'
                  buttonStyle='solid'
                  value={r.basis}
                  options={BASIS_OPTIONS}
                  onChange={(e) => patchRight(r.key, { basis: e.target.value })}
                />
                {r.basis === 'rule' && (
                  <span className='flex items-center gap-2'>
                    <Select
                      size='small'
                      className='w-60'
                      placeholder='Variable…'
                      value={r.rule_variable ?? undefined}
                      options={variableOptions}
                      onChange={(v) => patchRight(r.key, { rule_variable: v ?? null })}
                    />
                    <Select
                      size='small'
                      className='w-16'
                      value={r.rule_operator}
                      options={OPERATOR_OPTIONS}
                      onChange={(v) => patchRight(r.key, { rule_operator: v })}
                    />
                    <InputNumber
                      size='small'
                      className='w-28'
                      value={r.rule_threshold ?? undefined}
                      onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
                        patchRight(r.key, { rule_threshold: numberFromBlur(e) })
                      }
                    />
                    {ruleVariables.find((v) => v.key === r.rule_variable)?.unit === 'percent' && (
                      <span className='text-xs text-gray-400'>%</span>
                    )}
                  </span>
                )}
                {r.basis === 'right_reference' && (
                  <span className='flex items-center gap-2'>
                    <Select
                      size='small'
                      className='w-72'
                      allowClear
                      showSearch
                      optionFilterProp='label'
                      placeholder='Referenced right (e.g. a defined term)…'
                      value={r.referenced_right_id ?? undefined}
                      options={referenceOptions.filter((o) => o.value !== r.id)}
                      onChange={(v) => patchRight(r.key, { referenced_right_id: v ?? null })}
                    />
                    <Input
                      size='small'
                      className='w-52'
                      placeholder='Defined term (free text)'
                      value={r.reference_text ?? ''}
                      onChange={(e) => patchRight(r.key, { reference_text: e.target.value || null })}
                    />
                  </span>
                )}
                {r.basis === 'absolute' && (
                  <span className='text-xs text-gray-400'>
                    In force while the agreement set is in force.
                  </span>
                )}
              </div>
              <div className='flex items-center gap-2'>
                <Select
                  size='small'
                  className='w-80'
                  allowClear
                  showSearch
                  optionFilterProp='label'
                  placeholder='Link executed document…'
                  value={r.source_file_id ?? undefined}
                  options={documentOptions}
                  onChange={(v) => patchRight(r.key, { source_file_id: v ?? null })}
                />
                <Input
                  size='small'
                  className='w-64'
                  placeholder='…or paste a document URL'
                  value={r.source_url ?? ''}
                  onChange={(e) => patchRight(r.key, { source_url: e.target.value || null })}
                />
                <Input
                  size='small'
                  className='flex-1'
                  placeholder='Section ref (e.g. IRA §4.1)'
                  value={r.section_ref ?? ''}
                  onChange={(e) => patchRight(r.key, { section_ref: e.target.value || null })}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  )
}
